import React, { useContext, useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import { useTranslation } from "react-i18next";
import { ThemeContext } from "../../../..";
import ColdWaveAffectedPopupData from "./ColdwavePopup";
import { coldwavechartdata, coldwavepopupdata } from "../../../../Services/EndPoints";
import { getData, postDataWithBody } from "../../../../Services/Services";
import "./ColdwaveDashboard.css";

const TITLE_MAP = {
  number_of_deaths: "Loss of Life",
  number_of_bonfire_places: "Places Where Bonfire Lit",
  number_of_night_shelters: "Number of Night Shelters",
  number_of_ppl_taking_shelter_in_night_shelters: "People Taking Night Shelter",
  number_of_blankets_distributed: "Blankets Distributed",
};

const formatTitle = (key) =>
  TITLE_MAP[key] || (key || "").split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const ColdWaveBarChart = ({
  title, fromDate, toDate,
  selectedIncident, startDateStr, endDateStr,
  isTrendExpanded,
}) => {
  const { theme } = useContext(ThemeContext);
  const echartsDom = useRef(null);
  const chartRef = useRef(null);
  const { t } = useTranslation();

  const [labelColor, setLabelColor] = useState("#909097");
  const [modalData, setModalData] = useState(null);
  const [isShowModal, setIsShowModal] = useState(false);
  const [cardData, setCardData] = useState([]);
  const [popUpData, setPopUpData] = useState([]);

  useEffect(() => {
    setLabelColor(theme === "dark" ? "#fff" : "#909097");
  }, [theme]);

  const hideModal = () => { setIsShowModal(false); setModalData(null); setPopUpData([]); };

  const fetchBarData = async () => {
    try {
      const response = await getData(
        coldwavechartdata
          .replace("FROMDATE", startDateStr)
          .replace("TODATE", endDateStr)
          .replace("BARTYPE", selectedIncident)
      );
      setCardData(response || []);
    } catch (e) { console.error(e); }
  };

  const fetchBlockData = async (regionIds) => {
    try {
      const blockData = await postDataWithBody(coldwavepopupdata, {
        region_id_list: regionIds,
        from_date: startDateStr,
        to_date: endDateStr,
        bar_type: selectedIncident,
      }, {});

      const formatted = (blockData || []).map((item) => {
        const copy = { ...item };
        Object.keys(copy).forEach((k) => {
          if (typeof copy[k] === "number" && k !== "region_id")
            copy[k] = parseFloat(copy[k].toFixed(5));
        });
        return copy;
      }).filter((b) => b.count > 0);

      setPopUpData(formatted);
      setIsShowModal(true);
    } catch (e) {
      console.error(e);
      setPopUpData([]);
      setIsShowModal(true);
    }
  };

  useEffect(() => {
    if (selectedIncident) fetchBarData();
    else setCardData([]);
  }, [selectedIncident, startDateStr, endDateStr]);

  // Dynamic height based on bar count
  const validBarCount = cardData.filter(d => d.count > 0).length;
  const dynamicHeight = isTrendExpanded ? "calc(100vh - 160px)" : Math.max(150, Math.min(500, validBarCount * 45 + 80));

  const getChartOptions = (data, lColor) => {
    const sorted = [...data].filter((d) => d.count > 0).sort((a, b) => a.count - b.count);

    return {
      backgroundColor: "transparent",
      animation: true,
      animationDuration: 600,
      animationEasing: "cubicOut",
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "none" },
        backgroundColor: "#fff",
        borderColor: "rgba(42,101,145,.12)",
        borderWidth: 1,
        padding: [10, 14],
        textStyle: { color: "#0F2D42", fontSize: 12, fontFamily: "Inter, sans-serif" },
        formatter: (params) => {
          const p = params[0];
          return `<span style="font-weight:600">${p.name}</span><br/>
                  <span style="color:#2A6591;font-weight:700;font-size:14px">${p.value}</span>`;
        },
      },
      grid: { left: "2%", top: 20, right: "18%", bottom: 36, containLabel: true },
      xAxis: {
        type: "value",
        splitLine: { show: true, lineStyle: { color: "rgba(42,101,145,.07)", type: "dashed" } },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: lColor, fontSize: 11, fontFamily: "Inter, sans-serif", fontWeight: 600 },
      },
      yAxis: {
        type: "category",
        data: sorted.map((d) => d.dist_name),
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { color: lColor, fontSize: 11, fontFamily: "Inter, sans-serif", fontWeight: 600, overflow: "truncate", ellipsis: "…", width: 110 },
      },
      dataZoom: sorted.length > 10 ? [{
        type: "slider", yAxisIndex: 0, orient: "vertical",
        start: 0, end: Math.round((10 / sorted.length) * 100),
        width: 14, right: 8,
        borderColor: "transparent", backgroundColor: "rgba(42,101,145,.04)",
        fillerColor: "rgba(42,101,145,.12)",
        handleStyle: { color: "#2A6591", borderColor: "#2A6591" },
      }] : [],
      series: [{
        type: "bar",
        barWidth: 14,
        barMaxWidth: 18,
        barCategoryGap: "20%",
        data: sorted.map((item) => ({
          value: item.count,
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: "#4BA3D3CC" },
              { offset: 1, color: "#4BA3D3" },
            ]),
            borderRadius: [0, 8, 8, 0],
          },
          emphasis: {
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: "#4BA3D3" },
                { offset: 1, color: "#4BA3D3DD" },
              ]),
              shadowBlur: 8, shadowColor: "#4BA3D355",
            },
          },
        })),
        label: {
          show: true, position: "right", distance: 8,
          formatter: (p) => p.value > 0 ? `{val|${p.value}}` : "",
          rich: {
            val: {
              color: "#2A6591", fontSize: 11, fontWeight: 700,
              fontFamily: "Inter, sans-serif",
              backgroundColor: "rgba(42,101,145,.08)",
              borderRadius: 6, padding: [3, 8],
            },
          },
        },
      }],
    };
  };

  useEffect(() => {
    if (!echartsDom.current || cardData.length === 0) {
      if (chartRef.current) { try { chartRef.current.dispose(); } catch (_) {} chartRef.current = null; }
      return;
    }
    if (chartRef.current) { try { chartRef.current.dispose(); } catch (_) {} }
    const myChart = echarts.init(echartsDom.current);
    chartRef.current = myChart;
    const resize = () => { try { myChart.resize(); } catch (_) {} };
    window.addEventListener("resize", resize);
    return () => { window.removeEventListener("resize", resize); try { myChart.dispose(); } catch (_) {} chartRef.current = null; };
  }, [cardData.length]);

  // Resize after the expand/collapse CSS animation finishes (0.25s pop-in)
  useEffect(() => {
    const t = setTimeout(() => { try { chartRef.current?.resize(); } catch (_) {} }, 300);
    return () => clearTimeout(t);
  }, [isTrendExpanded]);

  useEffect(() => {
    const myChart = chartRef.current;
    if (!myChart || cardData.length === 0) return;
    try {
      myChart.clear();
      myChart.setOption(getChartOptions(cardData, labelColor));
      myChart.off("click");
      myChart.on("click", (params) => {
        if (params.componentType !== "series") return;
        const clicked = cardData.find((d) => d.dist_name === params.name);
        if (!clicked) return;
        setModalData({ district: clicked.dist_name, affectedCount: clicked.count });
        fetchBlockData(clicked.region_id.split(",").map(Number));
      });
    } catch (e) { console.warn(e); }
  }, [chartRef.current, labelColor, cardData]);

  if (!selectedIncident) return null;

  const hasData = cardData.filter(d => d.count > 0).length > 0;

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column" }}>
      {/* Always keep echartsDom in the DOM so ECharts never loses its container */}
      <div
        ref={echartsDom}
        style={{ height: hasData ? dynamicHeight : 0, width: "100%", display: hasData ? "block" : "none" }}
      />
      {!hasData && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: 32 }}>
          <svg viewBox="0 0 48 48" fill="none" width="44" height="44">
            <rect x="4" y="30" width="8" height="14" rx="2" fill="#C5DFF0" opacity=".5"/>
            <rect x="16" y="20" width="8" height="24" rx="2" fill="#A0C8E0" opacity=".5"/>
            <rect x="28" y="12" width="8" height="32" rx="2" fill="#7AB4D0" opacity=".5"/>
            <rect x="40" y="22" width="8" height="22" rx="2" fill="#5AA0C0" opacity=".5"/>
          </svg>
          <p style={{ fontSize: ".76rem", color: "#7A9BB0", fontWeight: 500, textAlign: "center" }}>
            No data available for the selected period
          </p>
        </div>
      )}

      {/* ── Styled Popup (replaces Bootstrap Modal) ── */}
      {isShowModal && (
        <div className="cwd-popup-overlay" onClick={hideModal}>
          <div className="cwd-popup" onClick={e => e.stopPropagation()}>
            <div className="cwd-popup-header">
              <div className="cwd-popup-header-left">
                <div className="cwd-popup-logo">
                  <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
                    <circle cx="10" cy="10" r="2.8" fill="white" opacity=".9"/>
                    <path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.34 4.34l1.42 1.42M14.24 14.24l1.42 1.42M4.34 15.66l1.42-1.42M14.24 5.76l1.42-1.42"
                      stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className="cwd-popup-overline">Cold Wave Impact Zone</div>
                  <div className="cwd-popup-title-text">
                    {modalData?.district} • {formatTitle(selectedIncident)}
                  </div>
                </div>
              </div>
              <button className="cwd-popup-close" onClick={hideModal}>✕</button>
            </div>

            <div className="cwd-popup-body">
              <ColdWaveAffectedPopupData blockData={popUpData} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColdWaveBarChart;